import type { ISummary } from '@/features/summary-widget';
import type { IProductEntity } from '@/entities';
import type {
  ICartCRUDItem,
  ICartFullApiService,
  ICartFullEntityResponse,
  ICartFullEntityResponseConfig,
  ProductMMCartFull,
  ICartLiteEntityResponse,
  TCartFullEntityTrackerPayload,
} from '../../models';

import { _CartStore } from './Cart.abstract';
import { ProductMM } from '@/entities';

export class _CartStoreFull extends _CartStore<ICartFullApiService, ICartFullEntityResponse> {
  public unavailableProducts = ref<ProductMMCartFull[]>([]);
  public deliveryPrices = ref<ProductMMCartFull[]>([]);
  public summary = ref<ISummary>();
  public config = ref<ICartFullEntityResponseConfig>();
  public hasRestrictions = ref(false);
  public _trackerPayload: TCartFullEntityTrackerPayload = {};

  #currentCityId = '1';
  set currentCityId(value: number) {
    this.#currentCityId = value.toString();
  }

  override handleResponse(cartResp: ICartFullEntityResponse) {
    if (this.isResponseOutdated(cartResp)) return;

    this.updatedAt.value = cartResp.updatedAt ?? Date.now();

    if (cartResp.config) {
      this.config.value = cartResp.config;
    }

    this.products.value = cartResp.products.map((it) => new ProductMM(it));
    this.unavailableProducts.value = cartResp.unavailableProducts.map((it) => new ProductMM(it));

    this.qnty.value =
      this.products.value.reduce((acc, it) => acc + it.product.amount, 0) +
      this.unavailableProducts.value.reduce((acc, it) => acc + it.product.amount, 0);

    this.summary.value = cartResp.summary;
    this.hasRestrictions.value = cartResp.hasRestrictions;

    this.cartResponse.response = cartResp;
    this._trackerPayload = cartResp._trackerPayload;
  }

  override reset() {
    super.reset();

    this.summary.value = undefined;
    this.unavailableProducts.value = [];
    this.deliveryPrices.value = undefined;
  }

  /** CRUDs */
  override async fetchCart(): Promise<any> {
    return this.APIService?.getCart(this.#currentCityId).then((resp) => this.handleResponse(resp));
  }

  override async addProductToCart(product: IProductEntity, qnty = 1): Promise<any> {
    const amountInCart = this.getProductBySkuId(product.skuId)?.product?.amount ?? 0;
    qnty += amountInCart;
    return this.APIService?.updateCartItems([{ product, qnty }], this.#currentCityId).then((resp) =>
      this.handleResponse(resp)
    );
  }

  override async patchCart(data: ICartCRUDItem[]): Promise<any> {
    if (!data.length) return this.products.value;

    return this.APIService?.updateCartItems(data, this.#currentCityId).then((resp) =>
      this.handleResponse(resp)
    );
  }

  public async removeUnavailableProducts() {
    const data: ICartCRUDItem[] = this.unavailableProducts.value.map((it) => ({
      product: it.product,
      qnty: 0,
    }));

    return this.patchCart(data);
  }

  public getLiteResponse(): ICartLiteEntityResponse | null {
    if (!this.products.value || !this.summary.value) return null;

    return {
      id: this.cartResponse.response.id,
      totalGoodsCount: this.qnty.value,
      totalBasePrice: this.summary.value.totalBasePrice,
      totalFinalPrice: this.summary.value.totalFinalPrice,
      updatedAt: this.cartResponse.response?.updatedAt ?? Date.now(),
      products: [
        ...this.products.value.map((it) => it.product),
        ...this.unavailableProducts.value.map((it) => it.product),
      ],
    };
  }
}
